/**
 * Client Auth Service
 * ====================
 * Handles client account creation, sign-in, and record linking.
 * Clients can optionally create email/password accounts to access
 * their exercises without needing the magic link.
 */

import { supabase } from '../lib/supabase'

export interface ClientAccountData {
  clientId: string
  email: string
  password: string
  fullName: string
}

export interface ClientSignInData {
  email: string
  password: string
}

export interface ClientInfo {
  id: string
  fullName: string
  email: string
  providerId: string
  accessToken: string
}

export interface CreateAccountResult {
  success: boolean
  error?: string
  userId?: string
}

export interface SignInResult {
  success: boolean
  error?: string
  client?: ClientInfo
}

export const clientAuthService = {
  /**
   * Create account for existing client and link records
   * This allows clients to access their exercises via login instead of magic link
   */
  async createClientAccount(data: ClientAccountData): Promise<CreateAccountResult> {
    try {
      // Step 1: Check if email already exists as a provider account
      const { data: existingUser, error: checkError } = await supabase
        .from('users')
        .select('id, role')
        .eq('email', data.email)
        .maybeSingle()

      if (checkError) {
        console.error('Error checking existing user:', checkError)
        // Don't fail here - the signup will fail if email exists
      }

      if (existingUser && existingUser.role !== 'client') {
        return {
          success: false,
          error: 'This email is already registered as a provider account. Please use a different email or contact support.',
        }
      }

      // Step 2: Create Supabase auth account with redirect to client dashboard
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            full_name: data.fullName,
            role: 'client',
          },
          emailRedirectTo: `${window.location.origin}/client/dashboard`,
        },
      })

      if (authError) {
        console.error('Auth signup error:', authError)
        return { success: false, error: authError.message }
      }

      if (!authData.user) {
        return { success: false, error: 'Account creation failed' }
      }

      // Step 3: Link client record to auth user AND set status to 'active'
      // Setting status to 'active' indicates the client has engaged with their portal
      const { error: linkError } = await supabase
        .from('clients')
        .update({
          user_id: authData.user.id,
          status: 'active'  // Mark as active when they create an account
        })
        .eq('id', data.clientId)

      if (linkError) {
        console.error('Failed to link client record:', linkError)
        // Note: The auth account was created but linking failed
        // This is not ideal but the user can try logging in and we can retry linking
        return {
          success: false,
          error: 'Account created but failed to link to your client record. Please try logging in.',
        }
      }

      // Step 4: Update user role to 'client' in the users table
      // This ensures the header shows client-specific navigation
      const { error: roleError } = await supabase
        .from('users')
        .update({ role: 'client' })
        .eq('id', authData.user.id)

      if (roleError) {
        console.error('Failed to update user role:', roleError)
        // Non-fatal - account is created and linked, role can be fixed later
      }

      return { success: true, userId: authData.user.id }
    } catch (err) {
      console.error('Unexpected error in createClientAccount:', err)
      return {
        success: false,
        error: 'An unexpected error occurred. Please try again.',
      }
    }
  },

  /**
   * Sign in as client and return client data
   */
  async signInAsClient(data: ClientSignInData): Promise<SignInResult> {
    try {
      const { data: authData, error: authError } =
        await supabase.auth.signInWithPassword({
          email: data.email,
          password: data.password,
        })

      if (authError) {
        console.error('Client sign in error:', authError)
        return { success: false, error: authError.message }
      }

      if (!authData.user) {
        return { success: false, error: 'Sign in failed' }
      }

      // Fetch linked client record
      const { data: client, error: clientError } = await supabase
        .from('clients')
        .select('id, full_name, email, provider_id, access_token, status')
        .eq('user_id', authData.user.id)
        .maybeSingle()

      if (clientError) {
        console.error('Error fetching client record:', clientError)
        return {
          success: false,
          error: 'Failed to load your client record. Please try again.',
        }
      }

      if (!client) {
        // User exists but no client record linked
        // This could happen if they signed up as a provider
        return {
          success: false,
          error: 'No client record linked to this account. If you are a provider, please use the main login.',
        }
      }

      // Check if client is archived or expired
      if (client.status === 'archived') {
        // Sign them out since they shouldn't have access
        await supabase.auth.signOut()
        return {
          success: false,
          error: 'Your account has been archived. Please contact your provider for assistance.',
        }
      }

      if (client.status === 'expired') {
        await supabase.auth.signOut()
        return {
          success: false,
          error: 'Your access has expired. Please contact your provider to renew.',
        }
      }

      // Ensure access_token exists before returning
      if (!client.access_token) {
        return {
          success: false,
          error: 'Client record is missing access token. Please contact your provider.',
        }
      }

      return {
        success: true,
        client: {
          id: client.id,
          fullName: client.full_name || '',
          email: client.email || '',
          providerId: client.provider_id,
          accessToken: client.access_token,
        },
      }
    } catch (err) {
      console.error('Unexpected error in signInAsClient:', err)
      return {
        success: false,
        error: 'An unexpected error occurred. Please try again.',
      }
    }
  },

  /**
   * Check if client already has an account linked
   */
  async hasAccount(clientId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('clients')
      .select('user_id')
      .eq('id', clientId)
      .single()

    if (error) {
      console.error('Error checking client account:', error)
      return false
    }

    return !!data?.user_id
  },

  /**
   * Get client by user_id (for authenticated clients)
   */
  async getClientByUserId(userId: string): Promise<ClientInfo | null> {
    const { data, error } = await supabase
      .from('clients')
      .select('id, full_name, email, provider_id, access_token')
      .eq('user_id', userId)
      .maybeSingle()

    if (error) {
      console.error('Error fetching client by user_id:', error)
      return null
    }

    if (!data) return null

    // Ensure access_token exists
    if (!data.access_token) {
      console.error('Client record is missing access token')
      return null
    }

    return {
      id: data.id,
      fullName: data.full_name || '',
      email: data.email || '',
      providerId: data.provider_id,
      accessToken: data.access_token,
    }
  },

  /**
   * Sign out the current client
   */
  async signOut(): Promise<void> {
    const { error } = await supabase.auth.signOut({ scope: 'global' })
    if (error) {
      console.error('Error signing out:', error)
      throw error
    }
  },
}
